import * as React from "react";
import { Tabs, Tab, Box, Card, CardContent, Typography } from "@mui/material";
import DecisionLogicTab from "../decisionLogic";
import SmartScrubTab from "./smartScrub";
import StorageUtils from "utils/StorageUtils";
import MoneyThumbTab from "./MoneyThumbTab";
import { useStoreActions, useStoreState } from "easy-peasy";
import { AccessTime } from "@mui/icons-material";
import LendPathWayTab from "./LendPathWayTab";
import OcrolusTab from "./ocrolus/ocrolusTab";

interface AnalysisToolsProps {
  dealApplicationId: number;
  permittedTabs: Record<string, string>;
  onViewTransactions?: (account: { accountnumber: string; monthyear: string }) => void;
  mcaSummary?: any;
  aiSummary?: any;
  setPositionsCount?: (count: number) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  noPadding?: boolean;
}

function CustomTabPanel({ children, value, index, noPadding = false, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`analysis-tabpanel-${index}`}
      aria-labelledby={`analysis-tab-${index}`}
      {...other}
      style={{ height: "100%" }}
    >
      {value === index && (
        <Box sx={{ p: noPadding ? 0 : 2, height: "100%" }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `analysis-tab-${index}`,
    "aria-controls": `analysis-tabpanel-${index}`,
  };
}

export default function AnalysisTools({
  dealApplicationId,
  permittedTabs,
  onViewTransactions,
  mcaSummary,
  aiSummary,
  setPositionsCount,
}: AnalysisToolsProps) {


  const getMediaAttachments = useStoreActions(
    (actions: any) => actions.fileUploads.getMediaAttachments
  );

  const tabLabels = [
    ...(StorageUtils.isFeatureEnabled("smartScrub") ? ["SmartScrub"] : []),
    ...(permittedTabs["decisionLogic"] !== "none" && StorageUtils.isFeatureEnabled("decision_logic")
      ? ["Decision Logic"]
      : []),
    ...(StorageUtils.isFeatureEnabled("lendPathway") ? ["Lend Pathway"] : []),
    ...(StorageUtils.isFeatureEnabled("moneyThumb") ? ["MoneyThumb"] : []),
    ...(StorageUtils.isFeatureEnabled("ocrolus") ? ["Ocrolus"] : []),
    ...(StorageUtils.isFeatureEnabled("hyperVerge") ? ["HyperVerge"] : []),
  ];

  const { setIsAnalysisLoaded, setSelectedTab} = useStoreActions((actions: any) => actions.dealApplication);
  const { selectedAnalysisTab} = useStoreState((actions: any) => actions.dealApplication);


  React.useEffect(() => {
       setIsAnalysisLoaded(true); 
       getMediaAttachments({ dealApplicationId: dealApplicationId })
    }
    , []);

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
      setSelectedTab(newValue);
    };
    const renderComingSoon = (label: string) => (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "500px",
          padding: 6,
          background: "linear-gradient(135deg, #f5f7fa 0%, #e4edf5 50%, #ffffff 100%)",
          borderRadius: 4,
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.02)",
          position: "relative",
          overflow: "hidden",
          fontFamily: "'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.05) 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.03) 0%, transparent 50%),
              radial-gradient(circle at 40% 40%, rgba(120, 220, 255, 0.04) 0%, transparent 50%)
            `,
            animation: "gradientShift 8s ease-in-out infinite",
          },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "10%",
            right: "10%",
            width: 120,
            height: 120,
            borderRadius: "50%",
            background:
              "linear-gradient(45deg, rgba(102, 126, 234, 0.04) 0%, rgba(118, 75, 162, 0.02) 100%)",
            animation: "float 8s ease-in-out infinite",
            filter: "blur(1px)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "15%",
            left: "8%",
            width: 100,
            height: 100,
            borderRadius: "50%",
            background:
              "linear-gradient(135deg, rgba(102, 126, 234, 0.03) 0%, rgba(118, 75, 162, 0.01) 100%)",
            animation: "float 10s ease-in-out infinite 2s",
            filter: "blur(1px)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: "60%",
            right: "20%",
            width: 80,
            height: 80,
            borderRadius: "50%",
            background:
              "linear-gradient(225deg, rgba(102, 126, 234, 0.02) 0%, rgba(118, 75, 162, 0.005) 100%)",
            animation: "float 12s ease-in-out infinite 1s",
            filter: "blur(1px)",
          }}
        />

        <Card
          sx={{
            maxWidth: 520,
            width: "100%",
            textAlign: "center",
            padding: { xs: 3, sm: 4, md: 5 },
            boxShadow: `
              0 0 0 1px rgba(255, 255, 255, 0.8),
              0 8px 32px -8px rgba(0, 0, 0, 0.12),
              0 16px 48px -16px rgba(0, 0, 0, 0.08),
              inset 0 1px 0 rgba(255, 255, 255, 0.9)
            `,
            background: "rgba(255, 255, 255, 0.85)",
            backdropFilter: "blur(20px) saturate(180%)",
            border: "1px solid rgba(255, 255, 255, 0.6)",
            borderRadius: 3,
            position: "relative",
            zIndex: 1,
            transform: "translateY(0)",
            transition: "all 0.4s cubic-bezier(0.22, 1, 0.36, 1)",
            "&:hover": {
              transform: "translateY(-8px)",
              boxShadow: `
                0 0 0 1px rgba(255, 255, 255, 0.9),
                0 20px 40px -12px rgba(0, 0, 0, 0.15),
                0 24px 64px -24px rgba(0, 0, 0, 0.1),
                inset 0 1px 0 rgba(255, 255, 255, 0.95)
              `,
            },
          }}
        >
          <CardContent sx={{ padding: { xs: 2, sm: 3 } }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                marginBottom: 4,
                position: "relative",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  width: 100,
                  height: 100,
                  background:
                    "linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.04) 100%)",
                  borderRadius: "50%",
                  animation: "pulse 3s ease-in-out infinite",
                  filter: "blur(8px)",
                }}
              />
              <Box
                sx={{
                  position: "relative",
                  padding: 3,
                  background: "linear-gradient(135deg, #ffffff 0%, #f8faff 100%)",
                  borderRadius: "50%",
                  border: "1px solid rgba(255, 255, 255, 0.8)",
                  boxShadow: `
                    0 8px 32px rgba(102, 126, 234, 0.12),
                    0 4px 16px rgba(0, 0, 0, 0.04),
                    inset 0 1px 0 rgba(255, 255, 255, 0.8)
                  `,
                }}
              >
                <AccessTime
                  sx={{
                    fontSize: 40,
                    color: "#667eea",
                    filter: "drop-shadow(0 2px 8px rgba(102, 126, 234, 0.3))",
                  }}
                />
              </Box>
            </Box>

            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                fontSize: { xs: "1.25rem", sm: "1.5rem" },
                color: "#2c3e50",
                marginBottom: 3,
                letterSpacing: "-0.01em",
              }}
            >
              {label}
            </Typography>
            
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "1.75rem", sm: "2rem", md: "2.25rem" },
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 50%, #2c3e50 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                marginBottom: 2,
                letterSpacing: "-0.02em",
                lineHeight: 1.2,
              }}
            >
              Coming Soon
            </Typography>

      
            <Box sx={{ width: "100%", marginBottom: 4 }}>
              <Box
                sx={{
                  width: "70%",
                  height: 6,
                  background: "rgba(102, 126, 234, 0.1)",
                  borderRadius: 3,
                  margin: "0 auto",
                  overflow: "hidden",
                  position: "relative",
                  boxShadow: "inset 0 1px 2px rgba(0, 0, 0, 0.05)",
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    background: "linear-gradient(90deg, #667eea 0%, #764ba2 50%, #a78bfa 100%)",
                    borderRadius: 3,
                    animation: "loading 3s ease-in-out infinite",
                    transform: "translateX(-100%)",
                    position: "relative",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      right: 0,
                      bottom: 0,
                      width: "20%",
                      background:
                        "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent)",
                      animation: "shimmer 3s ease-in-out infinite",
                    },
                  }}
                />
              </Box>
            </Box>

            <Typography
              variant="body2"
              sx={{
                color: "#7e8c9a",
                fontStyle: "italic",
                fontWeight: 300,
                letterSpacing: "0.02em",
                marginBottom: 3,
              }}
            >
              Stay tuned for the grand unveiling
            </Typography>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 1.5,
                marginTop: 3,
              }}
            >
              {[1, 2, 3].map((item) => (
                <Box
                  key={item}
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #667eea, #764ba2)",
                    animation: `bounce 1.8s infinite ${item * 0.3}s`,
                    boxShadow: "0 2px 8px rgba(102, 126, 234, 0.3)",
                  }}
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
   
  return (
    <Box sx={{ width: "100%", borderRadius: 2 }}>
      <Tabs
        value={selectedAnalysisTab}
        onChange={handleChange}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          minHeight: "36px",
          "& .MuiTabs-flexContainer": {
            display: "grid",
            gridAutoFlow: "column",
            gridAutoColumns: "max-content",
            justifyContent: "start",
            gap: 1,
          },
          "& .MuiTab-root": {
            textTransform: "none",
            fontFamily: "Roboto, sans-serif",
            fontWeight: 400,
            fontStyle: "normal",
            fontSize: "16px",
            minHeight: "32px",
            minWidth: "auto",
            padding: "4px 12px",
            borderRadius: "6px 6px 0 0",
            color: "#4b5563",
          },
          "& .Mui-selected": {
            color: "#2F929F",
            fontWeight: 400,
          },
          "& .MuiTabs-indicator": {
            backgroundColor: "#2F929F",
            height: "2px",
            borderRadius: "2px",
          },
        }}
      >
        {tabLabels.map((label, index) => (
          <Tab key={index} label={label} {...a11yProps(index)} />
        ))}
      </Tabs>

      {tabLabels.map((label, index) => (
        <CustomTabPanel key={index} value={selectedAnalysisTab} index={index}  noPadding={label === "Lend Pathway"} >
          {label === "SmartScrub" ? (
            <SmartScrubTab
              dealApplicationId={dealApplicationId}
              onViewTransactions={onViewTransactions}
              isReadOnly={permittedTabs["offerDetails"] === "read"}
              permittedTabs={permittedTabs}
            />
          ) : label === "Decision Logic" ? (
            <DecisionLogicTab dealApplicationId={dealApplicationId} />
          ) : label === "MoneyThumb" ? (
            <MoneyThumbTab
              dealApplicationId={dealApplicationId}
              isReadOnly={permittedTabs["offerDetails"] === "read"}
              permittedTabs={permittedTabs}
            />
          ) : (label === "Lend Pathway") ?
              <LendPathWayTab  dealApplicationId={dealApplicationId} />
              :
              (label === "Ocrolus") ?
              <OcrolusTab dealApplicationId={dealApplicationId}/>
              :
          (
            renderComingSoon(label)
          )}
        </CustomTabPanel>
      ))}
    </Box>
  );
}
